import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Utility } from '../helpers/utility';
import {
  IDatabaseGatewayApiConfig,
  IDatabaseGatewayApiConfigWrite,
  IDatabaseGatewayApiCredential,
  IDatabaseGatewayApiCredentialWrite,
  IDatabaseGatewayApiIdentifier,
  IDatabaseGatewayApiRevealedSecret,
  IDatabaseGatewayApiRecommendedSetup,
  IDatabaseGatewayApiRecommendedSetupWrite,
  IDatabaseGatewayApiSecret,
  IDatabaseGatewayApiValidation
} from '../models/IDatabaseGatewayApi';
import { IDatabaseGatewayApiOperation } from '../models/IDatabaseGatewayApiOperation';

export class DatabaseGatewayApiApis implements IDatabaseGatewayApiOperation {
  private constructor(
    private readonly httpClient: HttpClient,
    private readonly apiV1: string
  ) {}

  public static databaseGatewayApiOperation(
    httpClient: HttpClient,
    apiV1: string
  ): IDatabaseGatewayApiOperation {
    return new DatabaseGatewayApiApis(httpClient, apiV1);
  }

  public getBrandIdentifier(
    clientId: number,
    brandId: number
  ): Promise<IDatabaseGatewayApiIdentifier> {
    return this.get(clientId, brandId, '/identifier');
  }

  public updateBrandIdentifier(
    clientId: number,
    brandId: number,
    apiIdentifier: string
  ): Promise<IDatabaseGatewayApiIdentifier> {
    return this.send('patch', clientId, brandId, '/identifier', {
      apiIdentifier
    });
  }

  public listConfigs(
    clientId: number,
    brandId: number
  ): Promise<IDatabaseGatewayApiConfig[]> {
    return this.get(clientId, brandId, '/configs');
  }

  public createConfig(
    clientId: number,
    brandId: number,
    body: IDatabaseGatewayApiConfigWrite
  ): Promise<IDatabaseGatewayApiConfig> {
    return this.send('post', clientId, brandId, '/configs', body);
  }

  public setupRecommendedConfigs(
    clientId: number,
    brandId: number,
    body: IDatabaseGatewayApiRecommendedSetupWrite
  ): Promise<IDatabaseGatewayApiRecommendedSetup> {
    return this.send('post', clientId, brandId, '/configs/recommended', body);
  }

  public updateConfig(
    clientId: number,
    brandId: number,
    configId: number,
    body: IDatabaseGatewayApiConfigWrite
  ): Promise<IDatabaseGatewayApiConfig> {
    return this.send('patch', clientId, brandId, `/configs/${configId}`, body);
  }

  public validateConfig(
    clientId: number,
    brandId: number,
    body: IDatabaseGatewayApiConfigWrite
  ): Promise<IDatabaseGatewayApiValidation> {
    return this.send('post', clientId, brandId, '/configs/validate', body);
  }

  public listCredentials(
    clientId: number,
    brandId: number
  ): Promise<IDatabaseGatewayApiCredential[]> {
    return this.get(clientId, brandId, '/credentials');
  }

  public createCredential(
    clientId: number,
    brandId: number,
    body: IDatabaseGatewayApiCredentialWrite
  ): Promise<IDatabaseGatewayApiSecret> {
    return this.send('post', clientId, brandId, '/credentials', body);
  }

  public updateCredential(
    clientId: number,
    brandId: number,
    credentialId: number,
    body: IDatabaseGatewayApiCredentialWrite
  ): Promise<IDatabaseGatewayApiCredential> {
    return this.send(
      'patch',
      clientId,
      brandId,
      `/credentials/${credentialId}`,
      body
    );
  }

  public rotateCredential(
    clientId: number,
    brandId: number,
    credentialId: number
  ): Promise<IDatabaseGatewayApiSecret> {
    return this.send(
      'post',
      clientId,
      brandId,
      `/credentials/${credentialId}/rotate`,
      {}
    );
  }

  public revealCredential(
    clientId: number,
    brandId: number,
    credentialId: number
  ): Promise<IDatabaseGatewayApiRevealedSecret> {
    return this.get(clientId, brandId, `/credentials/${credentialId}/secret`);
  }

  private get<T>(clientId: number, brandId: number, path: string): Promise<T> {
    return lastValueFrom(
      this.httpClient.get<T>(this.url(clientId, brandId, path))
    );
  }

  private send<T>(
    method: 'post' | 'patch',
    clientId: number,
    brandId: number,
    path: string,
    body: unknown
  ): Promise<T> {
    return lastValueFrom(
      this.httpClient.request<T>(method, this.url(clientId, brandId, path), {
        body
      })
    );
  }

  private url(clientId: number, brandId: number, path: string): string {
    return Utility.buildURL({
      url: `${this.apiV1}/bff/database-gateway-api/brands/${brandId}${path}`,
      queryParameters: { clientId }
    });
  }
}
